import type { SxProps, Theme } from '@mui/material/styles';

type SxObject = Record<string, unknown>;

type VariantValue = string | number | boolean;

type BooleanKey = 'true' | 'false';

type VariantKey = string | number | BooleanKey;

type VariantDefinition = {
  base?: SxProps<Theme>;
  variants: Record<string, Record<VariantKey, SxProps<Theme>>>;
};

type VariantProps = Record<string, VariantValue>;

type InferVariantValue<T> = T extends Record<infer K, unknown>
  ? K extends BooleanKey
    ? boolean
    : K
  : never;

type ExtractVariantProps<T extends VariantDefinition> = {
  [K in keyof T['variants']]?: InferVariantValue<T['variants'][K]>;
};

/**
 * Utility type to extract variant props from a sxVariants configuration.
 *
 * Works like class-variance-authority's VariantProps by inferring prop types
 * directly from the variant configuration object.
 *
 * @example
 * const buttonStyles = sxVariants({
 *   base: { padding: 2 },
 *   variants: {
 *     variant: {
 *       primary: { backgroundColor: 'blue' },
 *       secondary: { backgroundColor: 'green' },
 *     },
 *     size: {
 *       small: { fontSize: 12 },
 *       large: { fontSize: 16 },
 *     },
 *     disabled: {
 *       true: { opacity: 0.4 },
 *       false: { opacity: 1 },
 *     },
 *   },
 * });
 *
 * type ButtonProps = SXVariantProps<typeof buttonStyles>;
 * // Inferred type:
 * // {
 * //   variant?: 'primary' | 'secondary';
 * //   size?: 'small' | 'large';
 * //   disabled?: boolean;
 * // }
 */

// biome-ignore lint/suspicious/noExplicitAny: unfortunately needed
type SXVariantProps<T> = T extends (props?: infer P) => any ? P : never;

const isObject = (value: unknown): value is SxObject => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/**
 * Deep merges two style objects, preserving nested properties
 * @internal
 */
const deepMerge = (target: SxObject, source: SxObject): SxObject => {
  const result = { ...target };

  for (const key in source) {
    if (Object.hasOwn(source, key)) {
      const sourceValue = source[key];
      const targetValue = result[key];

      if (isObject(sourceValue) && isObject(targetValue)) {
        result[key] = deepMerge(targetValue, sourceValue);
      } else {
        result[key] = sourceValue;
      }
    }
  }

  return result;
};

const mergeStyle = (acc: SxObject, style: SxProps<Theme> | undefined, theme?: Theme): SxObject => {
  if (!style) {
    return acc;
  }

  if (Array.isArray(style)) {
    return style.reduce<SxObject>((innerAcc, nestedStyle) => {
      return mergeStyle(innerAcc, nestedStyle, theme);
    }, acc);
  }

  if (typeof style === 'function') {
    if (!theme) {
      return acc;
    }

    const evaluated = style(theme);
    return mergeStyle(acc, evaluated, theme);
  }

  if (isObject(style)) {
    return deepMerge(acc, style);
  }

  return acc;
};

const mergeAllStyles = (styles: SxProps<Theme>[], theme?: Theme): SxObject => {
  return styles.reduce<SxObject>((acc, style) => mergeStyle(acc, style, theme), {});
};

/**
 * Combines multiple SxProps into a single SxProps with deep merging support.
 *
 * This function intelligently merges MUI sx props while preserving nested selectors,
 * responsive arrays, and theme functions. It provides a cleaner alternative to manual
 * style spreading and conditional logic.
 *
 * @param styles - Variable number of SxProps (objects, arrays, functions, or undefined)
 * @returns A merged SxProps object or function
 *
 * @example
 * // Basic merging
 * const styles = combineSx(
 *   { padding: 2, color: 'red' },
 *   { margin: 4, color: 'blue' }
 * );
 * // Result: { padding: 2, margin: 4, color: 'blue' }
 *
 * @example
 * // Deep merging nested selectors
 * const styles = combineSx(
 *   { '&:hover': { backgroundColor: 'grey', opacity: 0.8 } },
 *   { '&:hover': { color: 'white' } }
 * );
 * // Result: { '&:hover': { backgroundColor: 'grey', opacity: 0.8, color: 'white' } }
 *
 * @example
 * // Handling undefined values (useful for conditional styles)
 * const styles = combineSx(
 *   baseStyles,
 *   isActive && activeStyles,
 *   customSx
 * );
 *
 * @example
 * // Merging theme functions
 * const styles = combineSx(
 *   { padding: 2 },
 *   theme => ({ color: theme.palette.primary.main })
 * );
 *
 * @example
 * // Practical component usage
 * const Badge = ({ disabled, sx, ...props }) => (
 *   <Box
 *     sx={combineSx(
 *       baseStyles,
 *       disabled && { opacity: 0.4 },
 *       sx
 *     )}
 *     {...props}
 *   />
 * );
 *
 * @remarks
 * Benefits over manual sx management:
 * - Deep merging: Nested selectors like `&:hover` are merged
 * - Type safety: Full TypeScript support with proper inference
 * - Cleaner code: No need for spread operators or complex conditional logic
 * - Theme function support: Handles both static objects and theme callbacks
 * - Responsive arrays: Preserves MUI responsive value arrays
 * - Null-safe: Automatically filters out undefined values
 */
const combineSx = (
  ...styles: (SxProps<Theme> | SxProps<Theme>[] | undefined)[]
): SxProps<Theme> => {
  const allStyles = styles.flatMap<SxProps<Theme>>(style => {
    if (!style) return [];
    return Array.isArray(style) ? style : [style];
  });

  if (allStyles.length === 0) {
    return {};
  }

  const hasFunctions = allStyles.some(style => typeof style === 'function');

  if (!hasFunctions) {
    return mergeAllStyles(allStyles);
  }

  return (theme: Theme): SxObject => {
    return mergeAllStyles(allStyles, theme);
  };
};

/**
 * Converts variant values to keys for lookup
 * @internal
 */
const toVariantKey = (value: VariantValue): VariantKey => {
  return typeof value === 'boolean' ? (String(value) as BooleanKey) : value;
};

/**
 * Resolves variant definitions to an array of SxProps based on provided props
 * @internal
 */
const resolveVariants = (
  variantDef: VariantDefinition,
  variantProps: VariantProps,
): SxProps<Theme>[] => {
  const resolved: SxProps<Theme>[] = [];

  if (variantDef.base) {
    resolved.push(variantDef.base);
  }

  for (const variantKey in variantDef.variants) {
    if (Object.hasOwn(variantDef.variants, variantKey)) {
      const variantValue = variantProps[variantKey];
      if (variantValue !== undefined) {
        const lookupKey = toVariantKey(variantValue);
        const variantStyles = variantDef.variants[variantKey]?.[lookupKey];
        if (variantStyles) {
          resolved.push(variantStyles);
        }
      }
    }
  }

  return resolved;
};

/**
 * Creates a type-safe variant-based styling function for MUI components.
 *
 * Inspired by libraries like CVA (Class Variance Authority) and Stitches, this function
 * provides a declarative way to manage component styles with multiple variants.
 * It eliminates the need for complex conditional logic and provides excellent TypeScript support.
 *
 * @param config - Configuration object with base styles and variant definitions
 * @returns A function that accepts variant props and returns merged SxProps
 *
 * @example
 * // Define button styles with variants
 * const buttonStyles = sxVariants({
 *   base: {
 *     display: 'inline-flex',
 *     alignItems: 'center',
 *     borderRadius: 1,
 *     fontWeight: 600,
 *     transition: 'all 0.2s',
 *   },
 *   variants: {
 *     variant: {
 *       primary: {
 *         backgroundColor: 'primary.main',
 *         color: 'primary.contrastText',
 *         '&:hover': { backgroundColor: 'primary.dark' },
 *       },
 *       secondary: {
 *         backgroundColor: 'secondary.main',
 *         color: 'secondary.contrastText',
 *         '&:hover': { backgroundColor: 'secondary.dark' },
 *       },
 *       outlined: {
 *         border: '1px solid',
 *         borderColor: 'divider',
 *         '&:hover': { backgroundColor: 'action.hover' },
 *       },
 *     },
 *     size: {
 *       small: { padding: theme => theme.spacing(0.5, 1), fontSize: '0.875rem' },
 *       medium: { padding: theme => theme.spacing(1, 2), fontSize: '1rem' },
 *       large: { padding: theme => theme.spacing(1.5, 3), fontSize: '1.125rem' },
 *     },
 *     disabled: {
 *       true: { opacity: 0.6, pointerEvents: 'none' },
 *       false: {},
 *     },
 *   },
 * });

 * // Usage in component (fully type-safe)
 * const Button = ({ variant = 'primary', size = 'medium', disabled = false, sx, ...props }) => (
 *   <MuiButton sx={combineSx(buttonStyles({ variant, size, disabled }), sx)} {...props} />
 * );
 *
 * @remarks
 * Benefits of using sxVariants over manual conditional styling:
 *
 * 1. **Type Safety**: TypeScript infers variant props automatically
 *    - Autocomplete for variant values
 *    - Compile-time errors for invalid combinations
 *
 * 2. **Maintainability**: Centralized style definitions
 *    - All variants in one place
 *    - Easy to add/modify variants
 *    - No scattered conditional logic
 *
 * 3. **Performance**: Styles are computed once and reused
 *    - No inline style objects recreated on every render
 *    - Efficient variant resolution
 *
 * 4. **Readability**: Declarative over imperative
 *    - Clear intent with variant names
 *    - No complex ternaries or if-statements
 *
 * 5. **Flexibility**: Works seamlessly with combineSx
 *    - Can be combined with custom sx props
 *    - Supports all MUI sx features (theme functions, responsive arrays, etc.)
 *
 * @see {@link combineSx} for combining the returned styles with additional sx props
 */
const sxVariants =
  <T extends VariantDefinition>(config: T) =>
  (variantProps?: ExtractVariantProps<T>): SxProps<Theme> => {
    const props = (variantProps ?? {}) as VariantProps;
    const resolvedStyles = resolveVariants(config, props);
    return combineSx(...resolvedStyles);
  };

export { combineSx, sxVariants };
export type { SXVariantProps };
